"use client"

import * as React from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";

const moonUIAvatarVariantsPro = cva(
  "relative flex shrink-0 overflow-hidden",
  {
    variants: {
      size: {
        default: "h-10 w-10",
        xs: "h-6 w-6",
        sm: "h-8 w-8",
        md: "h-10 w-10",
        lg: "h-12 w-12",
        xl: "h-16 w-16",
        "2xl": "h-20 w-20",
      },
      radius: {
        default: "rounded-full",
        sm: "rounded-md",
        lg: "rounded-xl",
        full: "rounded-full",
        none: "rounded-none",
      },
      variant: {
        default: "",
        ring: "ring-2 ring-gray-300 dark:ring-gray-600",
        ringOffset: "ring-2 ring-gray-300 dark:ring-gray-600 ring-offset-2 ring-offset-background dark:ring-offset-gray-950",
        border: "border-2 border-gray-200 dark:border-gray-800"
      }
    },
    defaultVariants: {
      size: "default",
      radius: "default",
      variant: "default"
    },
  }
);

export interface MoonUIAvatarProProps 
  extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>,
    VariantProps<typeof moonUIAvatarVariantsPro> {}

const MoonUIAvatarPro = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  MoonUIAvatarProProps
>(({ className, size, radius, variant, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn(moonUIAvatarVariantsPro({ size, radius, variant }), className)}
    {...props}
  />
));
MoonUIAvatarPro.displayName = AvatarPrimitive.Root.displayName;

const MoonUIAvatarImagePro = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn("aspect-square h-full w-full", className)}
    {...props}
  />
));
MoonUIAvatarImagePro.displayName = AvatarPrimitive.Image.displayName;

const MoonUIAvatarFallbackPro = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      "flex h-full w-full items-center justify-center bg-muted",
      className
    )}
    {...props}
  />
));
MoonUIAvatarFallbackPro.displayName = AvatarPrimitive.Fallback.displayName;

// Avatar Group Component for displaying multiple avatars
interface MoonUIAvatarGroupProProps extends React.HTMLAttributes<HTMLDivElement> {
  max?: number;
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
  children?: React.ReactNode;
  overlapOffset?: number;
}

const MoonUIAvatarGroupPro = React.forwardRef<HTMLDivElement, MoonUIAvatarGroupProProps>(
  ({ className, max = 3, size = "md", children, overlapOffset, ...props }, ref) => {
    const childrenArray = React.Children.toArray(children);
    const visibleChildren = max ? childrenArray.slice(0, max) : childrenArray;
    const remainingCount = max ? Math.max(0, childrenArray.length - max) : 0;

    // Calculate overlap offset based on size
    const defaultOffsets = {
      xs: -4,
      sm: -6,
      md: -8,
      lg: -10,
      xl: -12,
      "2xl": -16,
    };
    const finalOffset = overlapOffset ?? defaultOffsets[size as keyof typeof defaultOffsets] ?? -8;

    return (
      <div
        ref={ref}
        className={cn("flex items-center", className)}
        {...props}
      >
        <div className="flex">
          {visibleChildren.map((child, index) => (
            <div 
              key={index} 
              className="relative ring-2 ring-background rounded-full"
              style={{ 
                marginLeft: index === 0 ? 0 : `${finalOffset}px`,
                zIndex: visibleChildren.length - index 
              }}
            >
              {React.isValidElement(child) && child.type === MoonUIAvatarPro
                ? React.cloneElement(child as React.ReactElement<any>, { size })
                : child}
            </div>
          ))}
          {remainingCount > 0 && (
            <div 
              className="relative z-0 ring-2 ring-background rounded-full"
              style={{ marginLeft: `${finalOffset}px` }}
            >
              <MoonUIAvatarPro size={size} className="bg-muted">
                <MoonUIAvatarFallbackPro className="text-xs font-medium">
                  +{remainingCount}
                </MoonUIAvatarFallbackPro>
              </MoonUIAvatarPro>
            </div>
          )}
        </div>
      </div>
    );
  }
);
MoonUIAvatarGroupPro.displayName = "MoonUIAvatarGroupPro";

export { MoonUIAvatarPro, MoonUIAvatarImagePro, MoonUIAvatarFallbackPro, MoonUIAvatarGroupPro };

// Backward compatibility exports
export { MoonUIAvatarPro as Avatar, MoonUIAvatarImagePro as AvatarImage, MoonUIAvatarFallbackPro as AvatarFallback, MoonUIAvatarGroupPro as AvatarGroup };